// src/components/hooks/useAccesoTrabajador.ts
//
// Resuelve si el trabajador ACTUAL (id_usuario + id_empresa de localStorage)
// tiene acceso a su panel. Mismas dos condiciones que en Profiles:
//   1) su empresa tiene plan efectivo, y
//   2) él tiene acceso (vista_trabajadores_con_acceso.tiene_acceso).
//
// Se usa en el guard de ruta de /panel-trabajador, para bloquear el acceso
// por URL directa (o inicio automático) además de la tarjeta de Profiles.
//
// Solo control de UX; la protección real va en RLS.

import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

interface AccesoTrabajador {
  cargando: boolean;
  tieneAcceso: boolean;
}

export function useAccesoTrabajador(): AccesoTrabajador {
  const [cargando, setCargando] = useState(true);
  const [tieneAcceso, setTieneAcceso] = useState(false);

  useEffect(() => {
    const idUsuario = localStorage.getItem('id_usuario') || '';
    const idEmpresa = localStorage.getItem('id_empresa') || '';

    if (!idUsuario || !idEmpresa) {
      setCargando(false);
      return;
    }

    let cancelado = false;

    (async () => {
      try {
        // 1) ¿La empresa tiene plan efectivo?
        const { data: plan, error: errPlan } = await supabase
          .from('vista_plan_efectivo_empresa')
          .select('id_plan_efectivo')
          .eq('id_empresa', idEmpresa)
          .maybeSingle();

        if (errPlan) throw errPlan;

        // 2) ¿El trabajador tiene acceso?
        const { data: acceso, error: errAcceso } = await supabase
          .from('vista_trabajadores_con_acceso')
          .select('tiene_acceso')
          .eq('id_usuario', idUsuario)
          .eq('id_empresa', idEmpresa)
          .maybeSingle();

        if (errAcceso) throw errAcceso;

        const ok = !!plan?.id_plan_efectivo && acceso?.tiene_acceso === true;

        if (!cancelado) {
          setTieneAcceso(ok);
          setCargando(false);
        }
      } catch (err) {
        Sentry.captureException(err, { extra: { idUsuario, idEmpresa } });
        // Ante error, no bloquear: dejar pasar y que la RLS proteja.
        if (!cancelado) {
          setTieneAcceso(true);
          setCargando(false);
        }
      }
    })();

    return () => {
      cancelado = true;
    };
  }, []);

  return { cargando, tieneAcceso };
}